/**
 * Unified ledger API service - customer and supplier ledgers share an identical
 * response shape on the backend (entries/total_count/balance_summary,
 * current_balance/transaction_counts/latest_transaction_date/total_transactions),
 * just under different URL prefixes. One service instead of two parallel ones.
 */
import apiClient from './api';

export type PartyType = 'customer' | 'supplier';

export interface LedgerEntry {
  id: string;
  transaction_date: string;
  transaction_type: string;
  reference_type: string;
  reference_number: string;
  reference_id: string;
  debit_amount: number;
  credit_amount: number;
  balance: number;
  description?: string;
}

export interface LedgerBalanceSummary {
  opening_balance: number;
  total_debits: number;
  total_credits: number;
  closing_balance: number;
}

export interface LedgerResponse {
  entries: LedgerEntry[];
  total_count: number;
  page: number;
  page_size: number;
  balance_summary: LedgerBalanceSummary;
}

export interface LedgerSummary {
  current_balance: number;
  transaction_counts: Record<string, number>;
  latest_transaction_date?: string;
  total_transactions: number;
}

export interface LedgerFilters {
  start_date?: string;
  end_date?: string;
  transaction_type?: string;
  page?: number;
  page_size?: number;
}

const basePath = (partyType: PartyType) =>
  partyType === 'customer' ? '/admin/customer-ledger' : '/admin/supplier-ledger';

export const LEDGER_TRANSACTION_TYPES: Array<{ value: string; label: string }> = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'payment', label: 'Payment' },
  { value: 'credit_note', label: 'Credit Note' },
  { value: 'debit_note', label: 'Debit Note' },
  { value: 'opening_balance', label: 'Opening Balance' },
  { value: 'adjustment', label: 'Adjustment' },
];

export class LedgerService {
  static async getLedger(partyType: PartyType, partyId: string, filters?: LedgerFilters): Promise<LedgerResponse> {
    const response = await apiClient.get(`${basePath(partyType)}/${partyId}`, { params: filters });
    return response.data;
  }

  static async getSummary(partyType: PartyType, partyId: string): Promise<LedgerSummary> {
    const response = await apiClient.get(`${basePath(partyType)}/${partyId}/summary`);
    return response.data;
  }

  /** PDF export only exists on the backend for customer ledgers today. */
  static async downloadLedgerPdf(
    partyId: string,
    filters?: Omit<LedgerFilters, 'page' | 'page_size'> & { include_summary?: boolean }
  ): Promise<Blob> {
    const response = await apiClient.get(`/admin/customer-ledger/${partyId}/pdf`, {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  }
}
